import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useDialogSearchParam } from "@/hooks/use-dialog-search-param";
import { useFilterSearchParams } from "@/hooks/use-filter-search-params";
import {
  usePagination,
  usePageOffset,
  DEFAULT_PAGE_SIZE,
} from "@/hooks/use-pagination";
import { useSizeClass } from "@/hooks/use-size-class";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CustomSelect } from "@/components/ui/custom-select";
import { EmptyState, Text } from "@/components/ui/text";
import { FilterToolbar } from "@/components/ui/filter-toolbar";
import { Icon } from "@/components/ui/icons";
import { PageTitle } from "@/contexts/page-title-context";
import { EntityStatusBadge } from "@/components/shared/entity-status-badge";
import { StickyHeaderSlot } from "@/components/ui/sticky-header-slot";
import { Heading } from "@/components/ui/heading";
import { VStack } from "@/components/ui/v-stack";
import { HStack } from "@/components/ui/h-stack";
import { LoadListTable } from "@/components/loads/load-list-table";
import { LoadListTableSkeleton } from "@/components/loads/load-list-table-skeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { PlaceBidModal } from "@/components/negotiations/place-bid-modal";
import { getListedLoads, getTrailerTypes, type Load } from "@/lib/api/loads";
import { formatDate } from "@/lib/formatting";
import { useUrlSelection } from "@/hooks/use-url-selection";
import { getCountries } from "@/lib/api/countries";
import { fromIntlLanguage } from "@/lib/i18n";
import { getLocalizedName } from "@/lib/localization";
import { highlight, highlightNumber } from "@/lib/highlight";
import { loadSearchableFields } from "@/lib/search/load";

function LoadBoardPage() {
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const sizeClass = useSizeClass();
  const isCompact = sizeClass === "compact";
  const { t: tCommon } = useTranslation("common");
  const { t: tLoadBoard } = useTranslation("loadBoard");
  const { t: tLoads } = useTranslation("loads");
  const { t: tNav } = useTranslation("nav");
  const navigate = useNavigate();

  // Filter state — read searchQuery before the query so `q` can be included in the key
  const { filters, setFilter, clearFilters } = useFilterSearchParams({
    keys: ["search", "origin", "trailer_type"],
  });
  const searchQuery = filters.search;
  const selectedOrigin = filters.origin;
  const selectedTrailerType = filters.trailer_type;

  // FilterToolbar already debounces draft → URL commits at 300ms, so
  // searchQuery here is the committed (debounced) value.
  const q = useMemo(() => {
    const trimmed = searchQuery.trim();
    return trimmed.length >= 3 ? trimmed : undefined;
  }, [searchQuery]);

  // Data state
  const { offset } = usePageOffset();
  const {
    data,
    isLoading: loading,
    isError,
    refetch,
  } = useQuery({
    queryKey: queryKeys.loadBoard({ limit: DEFAULT_PAGE_SIZE, offset, q }),
    queryFn: async () => {
      const [loadsResult, countriesResult, trailerTypesResult] =
        await Promise.all([
          getListedLoads({ limit: DEFAULT_PAGE_SIZE, offset, q }),
          getCountries({ limit: 1000 }),
          getTrailerTypes({ limit: 1000 }),
        ]);
      return {
        loads: loadsResult,
        countries: countriesResult,
        trailerTypes: trailerTypesResult,
      };
    },
  });
  const loads = useMemo(() => data?.loads.data ?? [], [data]);
  const total = data?.loads.total ?? 0;
  const countries = useMemo(() => data?.countries.data ?? [], [data]);
  const trailerTypes = useMemo(() => data?.trailerTypes.data ?? [], [data]);

  const { page, totalPages, setPage } = usePagination({ total });

  // Dialog state — close stale dialog if load not in current page
  const { dialog, openDialog, closeDialog } = useDialogSearchParam();
  const bidLoad = useMemo(
    () =>
      dialog?.type === "bid" && dialog.id
        ? (loads.find((l) => l.id === dialog.id) ?? null)
        : null,
    [dialog, loads],
  );
  useEffect(() => {
    if (dialog?.type === "bid" && dialog.id && !loading && !bidLoad) {
      closeDialog();
    }
  }, [dialog, loading, loads, bidLoad, closeDialog]);

  // Filter loads (search narrows server-side; only local filters for country/trailer)
  const filteredLoads = useMemo(() => {
    let filtered = loads;

    // Origin country filter
    if (selectedOrigin) {
      filtered = filtered.filter(
        (load) => load.country_origin.id === selectedOrigin,
      );
    }

    // Trailer type filter
    if (selectedTrailerType) {
      filtered = filtered.filter(
        (load) => load.trailer_type.id === selectedTrailerType,
      );
    }

    return filtered;
  }, [loads, selectedOrigin, selectedTrailerType]);

  // Get unique origin countries from loads
  const originCountries = useMemo(() => {
    const uniqueIds = new Set(loads.map((l) => l.country_origin.id));
    return countries.filter((c) => uniqueIds.has(c.id));
  }, [loads, countries]);

  // Get unique trailer types from loads
  const usedTrailerTypes = useMemo(() => {
    const uniqueIds = new Set(loads.map((l) => l.trailer_type.id));
    return trailerTypes.filter((tt) => uniqueIds.has(tt.id));
  }, [loads, trailerTypes]);

  const originOptions = originCountries.map((country) => ({
    value: country.id,
    label: getLocalizedName(country, language),
  }));
  const trailerTypeOptions = usedTrailerTypes.map((type) => ({
    value: type.id,
    label: getLocalizedName(type, language),
  }));

  const { selectedId: selectedLoadId, select: selectLoad } = useUrlSelection(
    "selectedBoardLoadId",
    filteredLoads,
    loading,
  );

  const handlePlaceBid = (load: Load) => {
    openDialog("bid", load.id);
  };

  const activeFilterCount =
    (selectedOrigin ? 1 : 0) + (selectedTrailerType ? 1 : 0);

  return (
    <VStack gap="lg" data-testid="load-board-page">
      {/* Header */}
      {/* load-board-header */}
      <PageTitle>{tNav("loadBoard")}</PageTitle>
      <StickyHeaderSlot>
        <Card
          data-testid="load-board-filters-card"
          variant="bordered"
          padding="xs"
        >
          <FilterToolbar
            data-testid="load-board-filters"
            searchPlaceholder={tLoadBoard("searchPlaceholder")}
            searchValue={searchQuery}
            onSearchChange={(v) => setFilter("search", v)}
            advancedFilters={[
              <CustomSelect
                key="origin-filter"
                value={selectedOrigin}
                onChange={(value) => setFilter("origin", value)}
                data-testid="load-board-origin-filter"
                options={originOptions}
                label={tLoadBoard("originCountry")}
                emptyLabel="filter"
              />,
              <CustomSelect
                key="trailer-type-filter"
                value={selectedTrailerType}
                onChange={(value) => setFilter("trailer_type", value)}
                data-testid="load-board-trailer-filter"
                options={trailerTypeOptions}
                label={tLoadBoard("trailerType")}
                emptyLabel="filter"
              />,
            ]}
            activeFilterCount={activeFilterCount}
            onClearFilters={clearFilters}
            pagination={{
              page,
              totalPages,
              perPage: DEFAULT_PAGE_SIZE,
              onPageChange: setPage,
              loading,
            }}
          />
        </Card>
      </StickyHeaderSlot>

      {/* Error State */}
      {!loading && isError && (
        <EmptyState
          data-testid="load-board-error"
          title={tCommon("error")}
          description={tCommon("errorLoadingData")}
          icon={<Icon.Alert color="danger" />}
        />
      )}

      {/* Main Content */}
      {!isError && (
        <>
          {loading ? (
            isCompact ? (
              <VStack>
                {Array.from({ length: 6 }).map((_, i) => (
                  <Card key={i} data-testid="load-board-card-skeleton">
                    <VStack className="animate-pulse">
                      <Skeleton className="h-4 w-24" />
                      <Skeleton className="h-5 w-48" />
                      <Skeleton className="h-4 w-32" />
                      <Skeleton className="h-10 w-full" />
                    </VStack>
                  </Card>
                ))}
              </VStack>
            ) : (
              <Card data-testid="load-board-table-card" padding="none">
                <LoadListTableSkeleton
                  headers={[
                    tLoads("loadId"),
                    tLoads("originDestination"),
                    tLoads("details"),
                    tLoads("columnDateTags"),
                    tLoads("columnRatePayment"),
                    tLoads("status"),
                    tLoads("actions"),
                  ]}
                  rows={6}
                />
              </Card>
            )
          ) : filteredLoads.length === 0 ? (
            <Card data-testid="load-board-table-card" padding="none">
              <VStack gap="none" padding="xl">
                <EmptyState
                  data-testid="load-board-empty"
                  title={
                    searchQuery || selectedOrigin || selectedTrailerType
                      ? tLoads("noLoadsTitle")
                      : tLoads("noListedLoadsTitle")
                  }
                  description={
                    searchQuery || selectedOrigin || selectedTrailerType
                      ? tLoadBoard("noLoadsMatch")
                      : tLoads("noListedLoadsDescription")
                  }
                  icon={<Icon.Loads />}
                />
              </VStack>
            </Card>
          ) : isCompact ? (
            /* Card list on compact */
            <VStack data-testid="load-board-card-list">
              {filteredLoads.map((load) => {
                const fields = loadSearchableFields(load, language);
                const currencySymbol = load.currency?.symbol || "$";

                return (
                  <Card
                    key={load.id}
                    data-testid={`load-board-card-${load.id}`}
                    className="space-y-3"
                    onClick={() => selectLoad(load.id)}
                  >
                    <HStack justify="between" gap="none">
                      <Text size="xs" weight="medium" color="muted">
                        {highlight(fields.load_id, q)}
                      </Text>
                      <EntityStatusBadge
                        entityType="load"
                        status={load.status}
                        searchable={fields.status}
                        q={q}
                      />
                    </HStack>
                    <Heading size="sm">
                      {fields.stop_origin_city.value
                        ? highlight(fields.stop_origin_city, q)
                        : "—"}
                      {" → "}
                      {fields.stop_destination_city.value
                        ? highlight(fields.stop_destination_city, q)
                        : "—"}
                    </Heading>
                    <Text size="xs" color="muted">
                      {formatDate(load.load_date, language)}
                    </Text>
                    <HStack justify="between" gap="none">
                      <Text size="xs" color="muted">
                        {highlight(fields.load_type, q)}
                        {" · "}
                        {highlight(fields.trailer_type, q)}
                        {" · "}
                        {load.tonnage}
                        {tCommon("tonnageUnit")}
                      </Text>
                      <Heading size="sm" className="!font-bold">
                        {currencySymbol}
                        {highlightNumber(fields.price, q)}
                      </Heading>
                    </HStack>
                    {/* Full-width action buttons */}
                    <VStack gap="sm">
                      {load.has_active_trade ? (
                        <Button
                          color="neutral"
                          variant="outline"
                          size="sm"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate({
                              to: `/negotiations?load=${load.id}`,
                            });
                          }}
                          data-testid={`load-action-viewNegotiations-${load.id}`}
                        >
                          {tLoads("openNegotiations")}
                        </Button>
                      ) : load.status === "Negotiated" ? (
                        <Button
                          color="neutral"
                          variant="outline"
                          size="sm"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate({
                              to: `/negotiations/history?load=${load.id}`,
                            });
                          }}
                          data-testid={`load-action-viewHistory-${load.id}`}
                        >
                          {tLoads("negotiationHistory")}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          fullWidth
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePlaceBid(load);
                          }}
                          data-testid={`load-action-placeBid-${load.id}`}
                        >
                          {tLoads("placeBid")}
                        </Button>
                      )}
                    </VStack>
                  </Card>
                );
              })}
            </VStack>
          ) : (
            /* Table on regular/expanded */
            <Card data-testid="load-board-table-card" padding="none">
              <LoadListTable
                loads={filteredLoads}
                q={searchQuery}
                selectedLoadId={selectedLoadId}
                showActions={true}
                showOffers={false}
                getLoadHref={(load) => `/loads/${load.id}`}
                onRowClick={(load) => {
                  selectLoad(load.id);
                }}
                actions={(load) =>
                  load.has_active_trade
                    ? [
                        {
                          key: "viewNegotiations",
                          label: tLoads("openNegotiations"),
                          color: "neutral" as const,
                          variant: "outline" as const,
                        },
                      ]
                    : load.status === "Negotiated"
                      ? [
                          {
                            key: "viewHistory",
                            label: tLoads("negotiationHistory"),
                            color: "neutral" as const,
                            variant: "outline" as const,
                          },
                        ]
                      : [
                          {
                            key: "placeBid",
                            label: tLoads("placeBid"),
                            color: "primary" as const,
                          },
                        ]
                }
                onActionClick={(action, load) => {
                  if (action === "placeBid") handlePlaceBid(load);
                  if (action === "viewNegotiations")
                    void navigate({ to: `/negotiations?load=${load.id}` });
                  if (action === "viewHistory")
                    void navigate({
                      to: `/negotiations/history?load=${load.id}`,
                    });
                }}
              />
            </Card>
          )}
        </>
      )}

      <PlaceBidModal
        open={!!bidLoad}
        load={bidLoad}
        onClose={closeDialog}
        onSuccess={() => {
          closeDialog();
          void refetch();
        }}
      />
    </VStack>
  );
}

export const Route = createFileRoute("/_app/load-board")({
  component: LoadBoardPage,
});
